<template>
   <div class="role-page" v-rect-loading="loading">
    <div class="role-header">
      <div class="role-title">{{ titleLabel }}</div>
    </div>
    <div class="transfer-container">
      <div class="form-container">
      <WrappedFrom
        ref="form"
        class="role-management-info"
        label-width="120px"
        :model="roleData"
        :formLines="formLines"
      >
      </WrappedFrom>
      </div>
      <el-transfer
        class="transfer"
        v-model="selectedPermissionIds"
        :data="permissionList"
        :titles="[this.$t(`${i18nPrefix}.label.permissionSource`), this.$t(`${i18nPrefix}.label.permissionTarget`)]"
      />
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ cancelBtnLabel }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ saveBtnLabel }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { selectLabelI18n } from '@/i18n'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { mixedRouterPush } from '@/router/routes'

export default {
  components: {
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'admin.company.roleManagement',
      permissionList: [],
      roleData: {},
      loading: false,
      selectedPermissionIds: []
    }
  },
  computed: {
    roleId () {
      return this.$route.params.roleId
    },
    cancelBtnLabel () {
      return selectLabelI18n(this, 'cancelBtn', 'common.btn.cancel')
    },
    saveBtnLabel () {
      return selectLabelI18n(this, 'saveBtn', 'common.btn.save')
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}${this.isEditMode ? '.title.roleEdit' : '.title.roleAdd'}`)
    },
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    formLines () {
      const lines = {
        title: {
          label: this.$t(`${this.i18nPrefix}.label.roleName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.roleName`),
          rules: [
            { required: true, message: this.$t(`${this.i18nPrefix}.message.roleNameRequired`), trigger: 'blur' },
            { validator: this.validateRoleName, trigger: 'blur' }
          ]
        }
      }
      return lines
    }
  },
  created () {
    this.initDate()
  },
  mounted () {},
  methods: {
    ...mapActions('roleManagement', ['getPermissionsList', 'roleNameValidate', 'addRole', 'getRole', 'updateRole']),

    async initDate () {
      try {
        this.loading = true

        if (this.isEditMode) {
          const params = {
          }
          await this.initPermissionsList(params)
          const resp = await this.getRole({ roleId: this.roleId, params })
          if (resp) {
            this.selectedPermissionIds = resp.selected_permission_id_list
            this.roleData = resp.role
          }
        } else {
          await this.initPermissionsList()
        }
      } finally {
        this.loading = false
      }
    },
    async initPermissionsList (params) {
      let resp
      if (params) {
        resp = await this.getPermissionsList(params)
      } else {
        resp = await this.getPermissionsList()
      }
      if (resp) {
        this.permissionList = _.map(resp, permission => ({
          key: permission.id,
          label: permission.description
        }))
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    },
    async validate () {
      let result = false
      result = await this.$refs.form.submit()
      return result
    },
    async handleSave () {
      if (this.loading || !await this.validate()) {
        return
      }
      const data = {
        title: this.roleData.title,
        select_permission_ids: this.selectedPermissionIds
      }
      try {
        this.loading = true
        if (this.isEditMode) {
          const resp = await this.updateRole({
            roleId: this.roleId,
            data
          })
          if (resp) {
            this.$message({
              message: this.$t(`${this.i18nPrefix}.message.optionSuccess`),
              type: 'success'
            })
            this.handleCancel()
          }
        } else {
          const resp = await this.addRole(data)
          if (resp) {
            this.$message({
              message: this.$t(`${this.i18nPrefix}.message.optionSuccess`),
              type: 'success'
            })
            this.handleCancel()
          }
        }
      } finally {
        this.loading = false
      }
    },
    async validateRoleName (rule, value, callback) {
      const params = {
        title: value
      }
      if (this.isEditMode) {
        params.role_id = this.roleId
      }
      const resp = await this.roleNameValidate(params)
      if (resp) {
        if (resp.validate) {
          callback()
        } else {
          callback(new Error(this.$t(`${this.i18nPrefix}.message.titleExists`)))
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.role-page {

  .role-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .role-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .transfer-container {
    max-width: 1020px;
    margin: 30px auto 0;
    text-align: center;

    .form-container {
      width: 500px;
      margin: 30px 110px;
    }

    .transfer {
      display: inline-block;
      text-align: left;

      ::v-deep {

        .el-transfer__buttons {
          padding: 0 25px;
        }

        .el-transfer-panel {
          width: 415px;
        }

        .el-transfer-panel__body {
          height: 460px;
        }

        .el-transfer-panel__list {
          height: 460px;
        }
      }
    }

    .transfer-head {
      text-align: left;
      font-size: 13px;
      margin-bottom: 10px;
      border-bottom: 1px solid #ddd;
      padding-bottom: 8px;
      color: #777;
    }

  }

  .bottom-btn-group {
    display: flex;
    justify-content: center;
    margin-top: 30px;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
